import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { User } from '../../../domain/user';
import { UserStoreService } from '../../../services/stores/user-store.service';
import {
   Component,
   DestroyRef,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { finalize } from 'rxjs';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';

import {
   HtmlReportCommentHistoryItem,
   HtmlReportCommentHistoryResponse
} from '../../../domain/html-report-annotations';
import { TaskService } from '../../../services/task.service';

export type ReportRowCommentAddedEvent = {
   rowId: string;
   comment: HtmlReportCommentHistoryItem;
};

@Component({
   selector: 'app-report-row-comments-modal',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      DialogModule,
      ButtonModule,
      InputTextareaModule,
      AutoCompleteModule
   ],
   templateUrl: './report-row-comments-modal.component.html',
   styleUrls: ['./report-row-comments-modal.component.scss']
})
export class ReportRowCommentsModalComponent implements OnChanges {
   @Input() visible = false;
   @Input() taskInstanceId: number | null = null;
   @Input() rowId: string | null = null;
   @Input() mentionsEnabled = false;

   @Output() visibleChange = new EventEmitter<boolean>();
   @Output() commentAdded = new EventEmitter<ReportRowCommentAddedEvent>();

   comments: HtmlReportCommentHistoryItem[] = [];
   commentDraft = '';

   selectedUsers: User[] = [];
   propertyUsers: User[] = [];
   filteredUsers: User[] = [];

   isHistoryLoading = false;
   isLoadingOlder = false;
   isSaving = false;

   historyError = '';
   saveError = '';

   hasMore = false;
   nextBeforeCreatedAtIso: string | null = null;
   nextBeforeEventId: number | null = null;

   private loadedContextKey: string | null = null;
   private requestVersion = 0;

   constructor(
      private taskService: TaskService,
      private userStore: UserStoreService,
      private destroyRef: DestroyRef
   ) {
      this.userStore.users
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((users) => {
            this.propertyUsers = users;
         });
   }

   ngOnChanges(changes: SimpleChanges): void {
      const contextChanged =
         !!changes['taskInstanceId'] || !!changes['rowId'] || !!changes['visible'];

      if (!contextChanged) return;

      if (!this.visible) {
         this.clearModalState();
         return;
      }

      const contextKey = this.getContextKey();

      if (!contextKey) return;

      if (contextKey !== this.loadedContextKey) {
         this.initializeModal(contextKey);
      }
   }

   get canAddComment(): boolean {
      return (
         !this.isSaving &&
         !!this.taskInstanceId &&
         !!this.rowId?.trim() &&
         !!this.commentDraft.trim()
      );
   }

   onDialogVisibleChange(nextVisible: boolean): void {
      if (!nextVisible) {
         this.closeModal();
      }
   }

   closeModal(): void {
      if (this.isSaving) return;

      this.clearModalState();
      this.visibleChange.emit(false);
   }

   retryHistory(): void {
      const contextKey = this.getContextKey();

      if (!contextKey) return;

      this.initializeModal(contextKey);
   }

   loadOlderComments(): void {
      const taskInstanceId = this.taskInstanceId;
      const rowId = this.rowId?.trim();

      if (
         !taskInstanceId ||
         !rowId ||
         !this.hasMore ||
         this.isLoadingOlder ||
         !this.nextBeforeCreatedAtIso ||
         this.nextBeforeEventId == null
      ) {
         return;
      }

      const currentRequestVersion = this.requestVersion;

      this.isLoadingOlder = true;
      this.historyError = '';

      this.taskService
         .getHtmlReportCommentHistory(
            taskInstanceId,
            rowId,
            50,
            this.nextBeforeCreatedAtIso,
            this.nextBeforeEventId
         )
         .pipe(
            finalize(() => {
               if (currentRequestVersion === this.requestVersion) {
                  this.isLoadingOlder = false;
               }
            })
         )
         .subscribe({
            next: (response) => {
               if (currentRequestVersion !== this.requestVersion) return;

               this.appendHistoryResponse(response);
            },
            error: (error: unknown) => {
               if (currentRequestVersion !== this.requestVersion) return;

               this.historyError = this.getErrorMessage(
                  error,
                  'Unable to load older comments. Please try again.'
               );
            }
         });
   }

   addComment(): void {
      const taskInstanceId = this.taskInstanceId;
      const rowId = this.rowId?.trim();
      const commentText = this.commentDraft.trim();

      if (!taskInstanceId || !rowId || !commentText || this.isSaving) {
         return;
      }

      this.isSaving = true;
      this.saveError = '';
      const requestVersion = this.requestVersion;

      const mentionUserIds = this.mentionsEnabled
         ? Array.from(
              new Set(
                 this.selectedUsers
                    .map((user) => user.id)
                    .filter((userId) => userId > 0)
              )
           )
         : [];

      this.taskService
         .addHtmlReportComment(taskInstanceId, {
            rowId,
            commentText,
            mentionUserIds
         })
         .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => {
               if (requestVersion === this.requestVersion) {
                  this.isSaving = false;
               }
            })
         )
         .subscribe({
            next: (comment) => {
               if (requestVersion !== this.requestVersion || !this.visible) return;

               this.comments = [
                  comment,
                  ...this.comments.filter((item) => item.eventId !== comment.eventId)
               ];

               this.commentDraft = '';
               this.selectedUsers = [];
               this.filteredUsers = [];

               this.commentAdded.emit({
                  rowId,
                  comment
               });

               // A successful HTTP response arrives before finalize() releases isSaving.
               this.isSaving = false;
               this.closeModal();
            },
            error: (error: unknown) => {
               if (requestVersion !== this.requestVersion || !this.visible) return;

               this.saveError = this.getErrorMessage(
                  error,
                  'Unable to add the comment. Your draft has been kept.'
               );
            }
         });
   }

   formatCommentDate(createdAtIso: string): string {
      const date = DateTime.fromISO(createdAtIso, {
         setZone: true
      });

      if (!date.isValid) {
         return createdAtIso;
      }

      return date.toLocal().toFormat('dd LLL yyyy, h:mm a');
   }

   getCommentActionLabel(item: HtmlReportCommentHistoryItem): string {
      return item.eventType === 'COMMENT_UPDATED' ? 'Updated by' : 'Added by';
   }

   getActorInitials(item: HtmlReportCommentHistoryItem): string {
      const source =
         item.actorDisplayName?.trim() ||
         item.actorEmail?.trim() ||
         `User ${item.actorUserId}`;

      const words = source
         .split(/\s+/)
         .map((word) => word.trim())
         .filter(Boolean);

      if (words.length === 0) return '?';

      if (words.length === 1) {
         return words[0].slice(0, 2).toUpperCase();
      }

      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
   }

   trackCommentByEventId(_index: number, item: HtmlReportCommentHistoryItem): number {
      return item.eventId;
   }

   filterUsers(event: { query?: string }): void {
      const query = event.query?.trim().toLowerCase() ?? '';
      const selectedUserIds = new Set(this.selectedUsers.map((user) => user.id));

      this.filteredUsers = this.propertyUsers.filter((user) => {
         if (selectedUserIds.has(user.id)) {
            return false;
         }

         return user.fullName.toLowerCase().startsWith(query);
      });
   }

   private initializeModal(contextKey: string): void {
      this.requestVersion++;
      this.loadedContextKey = contextKey;

      this.comments = [];
      this.commentDraft = '';
      this.selectedUsers = [];
      this.filteredUsers = [];
      this.isSaving = false;

      this.historyError = '';
      this.saveError = '';

      this.hasMore = false;
      this.nextBeforeCreatedAtIso = null;
      this.nextBeforeEventId = null;

      this.loadInitialHistory();
   }

   private loadInitialHistory(): void {
      const taskInstanceId = this.taskInstanceId;
      const rowId = this.rowId?.trim();

      if (!taskInstanceId || !rowId) return;

      const currentRequestVersion = this.requestVersion;

      this.isHistoryLoading = true;
      this.historyError = '';

      this.taskService
         .getHtmlReportCommentHistory(taskInstanceId, rowId, 50)
         .pipe(
            finalize(() => {
               if (currentRequestVersion === this.requestVersion) {
                  this.isHistoryLoading = false;
               }
            })
         )
         .subscribe({
            next: (response) => {
               if (currentRequestVersion !== this.requestVersion) return;

               this.applyInitialHistoryResponse(response);
            },
            error: (error: unknown) => {
               if (currentRequestVersion !== this.requestVersion) return;

               this.historyError = this.getErrorMessage(
                  error,
                  'Unable to load the comment history. Please try again.'
               );
            }
         });
   }

   private applyInitialHistoryResponse(
      response: HtmlReportCommentHistoryResponse
   ): void {
      this.comments = response.items ?? [];
      this.applyPaginationResponse(response);
   }

   private appendHistoryResponse(response: HtmlReportCommentHistoryResponse): void {
      const existingEventIds = new Set(
         this.comments.map((comment) => comment.eventId)
      );

      const olderComments = (response.items ?? []).filter(
         (comment) => !existingEventIds.has(comment.eventId)
      );

      this.comments = [...this.comments, ...olderComments];
      this.applyPaginationResponse(response);
   }

   private applyPaginationResponse(response: HtmlReportCommentHistoryResponse): void {
      this.hasMore = response.hasMore;
      this.nextBeforeCreatedAtIso = response.nextBeforeCreatedAtIso ?? null;
      this.nextBeforeEventId = response.nextBeforeEventId ?? null;
   }

   private getContextKey(): string | null {
      const taskInstanceId = this.taskInstanceId;
      const rowId = this.rowId?.trim();

      if (!taskInstanceId || !rowId) return null;

      return `${taskInstanceId}::${rowId}`;
   }

   private clearModalState(): void {
      this.requestVersion++;
      this.loadedContextKey = null;

      this.comments = [];
      this.commentDraft = '';
      this.selectedUsers = [];
      this.filteredUsers = [];

      this.isHistoryLoading = false;
      this.isLoadingOlder = false;
      this.isSaving = false;

      this.historyError = '';
      this.saveError = '';

      this.hasMore = false;
      this.nextBeforeCreatedAtIso = null;
      this.nextBeforeEventId = null;
   }

   private getErrorMessage(error: unknown, fallback: string): string {
      if (typeof error !== 'object' || error === null || !('error' in error)) {
         return fallback;
      }

      const responseBody = (error as { error?: unknown }).error;

      if (typeof responseBody === 'string' && responseBody.trim().length > 0) {
         return responseBody.trim();
      }

      return fallback;
   }
}
